import config from '../config'

// --- Score helpers ---

export function getScoreColor(score) {
  if (score >= 90) return 'text-green-600'
  if (score >= 70) return 'text-blue-600'
  if (score >= 50) return 'text-yellow-600'
  return 'text-red-500'
}

export function getScoreBg(score) {
  if (score >= 90) return 'bg-green-50 text-green-700 border-green-200'
  if (score >= 70) return 'bg-blue-50 text-blue-700 border-blue-200'
  if (score >= 50) return 'bg-yellow-50 text-yellow-700 border-yellow-200'
  return 'bg-red-50 text-red-700 border-red-200'
}

export function getScoreBarColor(score) {
  if (score >= 90) return 'bg-green-500'
  if (score >= 70) return 'bg-blue-500'
  if (score >= 50) return 'bg-yellow-500'
  return 'bg-red-400'
}

export function getScoreLabel(score) {
  if (score >= 85) return 'Excellent'
  if (score >= 70) return 'Good'
  if (score >= 50) return 'Partial'
  return 'Poor'
}

// --- Plan helpers ---

export function getPlanColor(plan) {
  const colors = {
    free: 'badge-gray',
    starter: 'badge-blue',
    pro: 'badge-green',
    business: 'badge-purple',
    enterprise: 'badge-yellow',
    admin: 'badge-purple',
  }
  return colors[plan] || 'badge-gray'
}

export function getPlanLabel(plan) {
  const normalizedPlan = String(plan || '').trim().toLowerCase()
  if (normalizedPlan === 'admin') return 'Admin'
  return config.PLANS[normalizedPlan]?.name || 'Free'
}

export function canAccessFeature(plan, feature) {
  const access = {
    export: ['starter', 'pro', 'business', 'enterprise', 'admin'],
    compare: ['pro', 'business', 'enterprise', 'admin'],
    team: ['business', 'enterprise', 'admin'],
    bulkUpload: ['pro', 'business', 'enterprise', 'admin'],
    priorityProcessing: ['business', 'enterprise', 'admin'],
    api: ['enterprise', 'admin'],
  }
  return access[feature]?.includes(plan) || false
}

// --- Date helpers ---

export function formatDate(dateString, locale = 'en') {
  if (!dateString) return ''
  try {
    const dateLocale = locale === 'fr' ? 'fr-FR' : 'en-GB'
    return new Date(dateString).toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

export function formatDateTime(dateString) {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

export function timeAgo(dateString, locale = 'en') {
  if (!dateString) return ''
  try {
    const seconds = Math.floor((Date.now() - new Date(dateString)) / 1000)
    const isFrench = locale === 'fr'
    if (seconds < 60) return isFrench ? "à l'instant" : 'just now'
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60)
      return isFrench ? `il y a ${minutes} min` : `${minutes}m ago`
    }
    if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600)
      return isFrench ? `il y a ${hours} h` : `${hours}h ago`
    }
    if (seconds < 604800) {
      const days = Math.floor(seconds / 86400)
      return isFrench ? `il y a ${days} j` : `${days}d ago`
    }
    return formatDate(dateString, locale)
  } catch {
    return ''
  }
}

export function formatResetDate(dateString) {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
    })
  } catch {
    return ''
  }
}

// --- File helpers ---

export function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function getFileExtension(fileName) {
  return fileName?.split('.').pop()?.toLowerCase() || ''
}

export function isValidCVFile(file) {
  const validTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ]
  const validExtensions = ['pdf', 'docx', 'doc']
  return (
    validTypes.includes(file.type) ||
    validExtensions.includes(getFileExtension(file.name))
  )
}

// --- String helpers ---

export function truncate(str, length = 50) {
  if (!str) return ''
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function capitalize(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function initials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

export function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

// --- Number helpers ---

export function formatNumber(num) {
  if (num === -1) return 'Unlimited'
  if (!num && num !== 0) return '0'
  return new Intl.NumberFormat('en-GB').format(num)
}

export function formatCurrency(amount, currency = 'EUR') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

export function percentage(value, total) {
  if (!total || total === 0) return 0
  return Math.min(100, Math.round((value / total) * 100))
}

// --- Array helpers ---

export function sortByScore(candidates) {
  return [...candidates].sort(
    (a, b) => (b.matchScore || 0) - (a.matchScore || 0)
  )
}

export function groupBy(array, key) {
  return array.reduce((groups, item) => {
    const group = item[key] || 'other'
    groups[group] = groups[group] || []
    groups[group].push(item)
    return groups
  }, {})
}

// --- Local storage helpers ---

export function safeLocalStorage(key, fallback = null) {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : fallback
  } catch {
    return fallback
  }
}

export function setLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    console.warn('localStorage not available')
  }
}

// --- URL helpers ---

export function getQueryParam(param) {
  return new URLSearchParams(window.location.search).get(param)
}

export function buildQueryString(params) {
  return Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
}
